import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout';
import DealerLayout from '../components/DealerLayout';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { C } from '../theme';
import InvoiceModal from '../components/InvoiceModal';

const TABS = [
  { key: 'all',     label: 'All'     },
  { key: 'offline', label: 'Offline' },
  { key: 'online',  label: 'Online'  },
];

export default function SalesHistory() {
  const { user } = useAuth();
  const isDealer = user?.role === 'dealer';
  const LayoutComp = isDealer ? DealerLayout : Layout;
  const [payments, setPayments] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('all');
  const [search,   setSearch]   = useState('');
  const [invoice,  setInvoice]  = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/api/payments');
        // Sales History only shows product sale payments (not AMC renewals)
        setPayments(r.data.filter(p => p.entry_type !== 'renewal'));
      }
      catch {} finally { setLoading(false); }
    })();
  }, []);

  const summary = useMemo(() => {
    const total   = payments.reduce((s, p) => s + (p.amount_paid || 0), 0);
    const online  = payments.filter(p => p.payment_mode === 'online').reduce((s, p) => s + (p.amount_paid || 0), 0);
    const offline = payments.filter(p => p.payment_mode === 'offline').reduce((s, p) => s + (p.amount_paid || 0), 0);
    return { total, online, offline, count: payments.length };
  }, [payments]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return payments.filter(p => {
      if (tab !== 'all' && p.payment_mode !== tab) return false;
      if (!q) return true;
      return [p.payment_id, p.client_name, p.client_ref, p.package_ref, p.installer_name, p.payment_method, p.payment_mode, p.entry_type, p.mapping_id]
        .some(v => v?.toLowerCase?.()?.includes?.(q));
    });
  }, [payments, tab, search]);

  const tabCount = useMemo(() => {
    const c = { all: payments.length };
    payments.forEach(p => { c[p.payment_mode] = (c[p.payment_mode] || 0) + 1; });
    return c;
  }, [payments]);

  return (
    <LayoutComp title="Sales History">
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))', gap: '14px', marginBottom: '22px' }}>
        <SCard icon="💰" label="Total Sales Collected" value={`₹${summary.total.toLocaleString('en-IN')}`}   sub={`${summary.count} transactions`} dot={C.success} bg={C.successBg} color={C.successText} />
        <SCard icon="🌐" label="Online Received"       value={`₹${summary.online.toLocaleString('en-IN')}`}  sub={`${payments.filter(p=>p.payment_mode==='online').length} txns`} dot="#7C3AED" bg="#EDE9FE" color="#5B21B6" />
        <SCard icon="💵" label="Offline Received"      value={`₹${summary.offline.toLocaleString('en-IN')}`} sub={`${payments.filter(p=>p.payment_mode==='offline').length} txns`} dot={C.primary} bg={C.primaryBg} color={C.primaryDeep} />
      </div>

      {/* Table Card */}
      <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>

        {/* Tabs + Search */}
        <div style={{ padding: '16px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '4px', background: '#F3F4F6', borderRadius: '10px', padding: '4px' }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: '6px 14px', borderRadius: '7px', fontSize: '12px', fontWeight: 700,
                border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                background: tab === t.key ? C.white : 'transparent',
                color:      tab === t.key ? C.textMain : C.textSub,
                boxShadow:  tab === t.key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
              }}>
                {t.label}
                {(tabCount[t.key] || 0) > 0 && (
                  <span style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '20px', fontSize: '10px', background: tab === t.key ? C.primaryBg : '#E5E7EB', color: tab === t.key ? C.primaryDeep : C.textSub }}>
                    {tabCount[t.key] || 0}
                  </span>
                )}
              </button>
            ))}
          </div>
          <input placeholder="Search payment ID, client, method…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '8px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
          {search && <button onClick={() => setSearch('')} style={{ padding: '8px 14px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Clear</button>}
          <span style={{ fontSize: '12px', color: C.textSub, whiteSpace: 'nowrap' }}>{filtered.length} records</span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '950px' }}>
            <thead>
              <tr style={{ background: C.primaryBg }}>
                {['Invoice ID', 'Client', 'Package', 'Type', 'Mode', 'Method', 'Amount Paid', 'Balance After', 'Date', 'Notes', ''].map(h => (
                  <th key={h} style={{ padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: '60px', color: C.textSub }}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: '60px' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>📊</div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: C.textMain }}>No payment records</div>
                  <div style={{ fontSize: '12px', color: C.textSub, marginTop: '4px' }}>
                    {payments.length === 0 ? 'Process payments in Credit List to see history here.' : 'No records match the filter.'}
                  </div>
                </td></tr>
              ) : filtered.map((p, i) => {
                const isOnline  = p.payment_mode === 'online';
                const isRenewal = p.entry_type === 'renewal';
                const isPartial = p.is_partial || (p.balance_due > 0.01);
                return (
                  <tr key={p.id} style={{ background: i % 2 === 0 ? C.white : '#FAFAFA', borderBottom: `1px solid ${C.border}` }}>
                    {/* Invoice ID */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 800, color: '#065F46', background: '#ECFDF5', padding: '3px 8px', borderRadius: '6px', display: 'inline-block' }}>
                          {p.payment_id}
                        </span>
                        <span style={{
                          fontSize: '10px', fontWeight: 800, padding: '2px 7px', borderRadius: '20px', display: 'inline-block',
                          background: isPartial ? '#FEF3C7' : '#D1FAE5',
                          color:      isPartial ? '#92400E'  : '#065F46',
                        }}>
                          {isPartial ? 'PARTIAL' : 'FULL'}
                        </span>
                      </div>
                    </td>
                    {/* Client */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, color: '#fff' }}>
                          {(p.client_name || 'C')[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>{p.client_name}</div>
                          <div style={{ fontSize: '11px', color: C.textSub }}>{p.client_ref}</div>
                        </div>
                      </div>
                    </td>
                    {/* Package */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: '#7C3AED' }}>{p.package_ref}</div>
                      <div style={{ fontSize: '11px', color: C.textSub }}>{p.mapping_id}</div>
                    </td>
                    {/* Type */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        background: isRenewal ? '#EDE9FE' : C.primaryBg,
                        color:      isRenewal ? '#5B21B6'   : C.primaryDeep,
                        padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                      }}>
                        {isRenewal ? '🔄 Renewal' : '📦 Sales'}
                      </span>
                    </td>
                    {/* Mode */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        background: isOnline ? '#EDE9FE' : '#FEF3C7',
                        color:      isOnline ? '#5B21B6'   : '#92400E',
                        padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                      }}>
                        {isOnline ? '🌐 Online' : '💵 Offline'}
                      </span>
                    </td>
                    {/* Method */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle', fontSize: '12px', color: C.textMain, fontWeight: 600 }}>
                      {p.payment_method || '—'}
                    </td>
                    {/* Amount Paid */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      <div style={{ fontSize: '15px', fontWeight: 900, color: '#065F46' }}>
                        ₹{(p.amount_paid || 0).toLocaleString('en-IN')}
                      </div>
                    </td>
                    {/* Balance After */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      {p.balance_due > 0.01 ? (
                        <span style={{ background: '#FEF3C7', color: '#92400E', padding: '3px 9px', borderRadius: '8px', fontSize: '12px', fontWeight: 700 }}>
                          ₹{p.balance_due.toLocaleString('en-IN')}
                        </span>
                      ) : (
                        <span style={{ background: '#D1FAE5', color: '#065F46', padding: '3px 9px', borderRadius: '8px', fontSize: '11px', fontWeight: 700 }}>
                          ✓ Cleared
                        </span>
                      )}
                    </td>
                    {/* Date */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle', fontSize: '12px', color: C.textSub, whiteSpace: 'nowrap' }}>
                      {p.created_at}
                    </td>
                    {/* Notes */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle', fontSize: '12px', color: C.textSub, maxWidth: '160px' }}>
                      {p.notes ? (
                        <span style={{ fontStyle: 'italic' }}>"{p.notes}"</span>
                      ) : '—'}
                    </td>
                    {/* View Invoice */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => setInvoice(p)} style={{
                          padding: '6px 12px', borderRadius: '7px', fontSize: '12px', fontWeight: 700,
                          background: '#ECFDF5', color: '#065F46', border: '1px solid #6EE7B7', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: '4px',
                        }}>
                          🧾 View
                        </button>
                        <button title="Send to client — coming soon" style={{
                          padding: '6px 12px', borderRadius: '7px', fontSize: '12px', fontWeight: 700,
                          background: '#F3F4F6', color: C.textSub, border: `1px solid ${C.border}`, cursor: 'not-allowed', opacity: 0.6,
                          display: 'flex', alignItems: 'center', gap: '4px',
                        }}>
                          ✉️ Send
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {filtered.length > 0 && (
          <div style={{ padding: '14px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center', background: '#FAFAFA' }}>
            <span style={{ fontSize: '12px', color: C.textSub, fontWeight: 600 }}>SHOWING {filtered.length} OF {payments.length} RECORDS</span>
            <span style={{ color: C.textSub }}>|</span>
            <FTotal label="Filtered Total" val={filtered.reduce((s, p) => s + (p.amount_paid || 0), 0)} color={C.success} bold />
          </div>
        )}
      </div>

      <InvoiceModal payment={invoice} onClose={() => setInvoice(null)} />
    </LayoutComp>
  );
}

function SCard({ icon, label, value, sub, dot, bg, color }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
        <span style={{ fontSize: '22px' }}>{icon}</span>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot, marginTop: '4px' }} />
      </div>
      <div style={{ fontSize: '22px', fontWeight: 800, color: '#111', marginBottom: '4px' }}>{value}</div>
      <div style={{ fontSize: '12px', fontWeight: 700, color, background: bg, display: 'inline-block', padding: '2px 8px', borderRadius: '6px', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '11px', color: '#888' }}>{sub}</div>
    </div>
  );
}

function FTotal({ label, val, color, bold }) {
  return (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
      <span style={{ fontSize: '12px', color: C.textSub }}>{label}:</span>
      <span style={{ fontSize: bold ? '14px' : '13px', fontWeight: bold ? 800 : 700, color }}>
        ₹{(val || 0).toLocaleString('en-IN')}
      </span>
    </div>
  );
}
